/**
 * "Tone in headphones when rest ends", in Settings > Rest timer.
 *
 * The rest notification's own sound plays on the notification stream, which
 * doesn't duck music — so with Spotify in your earbuds it is simply not heard.
 * The tone (RestTone.java) plays over the music, dipping it for a second, and
 * only when headphones are connected.
 *
 * Android only, and hidden when the plugin doesn't answer: an APK from before
 * the tone existed must not show a switch that does nothing. Whether headphones
 * are connected is asked again on return to the foreground, because the usual
 * way to fix "not connected" is to leave the app and put your earbuds in.
 */

import { useCallback, useEffect, useState } from 'react'

import {
  restToneStatus,
  setRestTone,
  testRestTone,
  type RestToneStatus,
} from '../native/notify'

export function RestTone() {
  // undefined = still asking, null = not available here.
  const [status, setStatus] = useState<RestToneStatus | null | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState(false)

  const refresh = useCallback(() => {
    let cancelled = false
    void restToneStatus().then((s) => { if (!cancelled) setStatus(s) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const stop = refresh()
    const onVisible = () => { if (!document.hidden) refresh() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [refresh])

  if (!status) return null

  const toggle = async (enabled: boolean) => {
    setBusy(true)
    setError(false)
    setStatus({ ...status, enabled })
    const saved = await setRestTone(enabled)
    if (saved === null) {
      setStatus({ ...status, enabled: !enabled })
      setError(true)
    }
    setBusy(false)
  }

  const test = async () => {
    setTesting(true)
    await testRestTone()
    setTesting(false)
    refresh()
  }

  return (
    <div style={{ marginTop: '.8rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', margin: 0 }}>
        <input
          type="checkbox"
          checked={status.enabled}
          disabled={busy}
          onChange={(e) => void toggle(e.target.checked)}
          style={{ width: 'auto' }}
        />
        Tone in headphones when rest ends
      </label>
      {error && (
        <div className="error" style={{ marginTop: '.5rem' }}>
          Couldn't save that — try again.
        </div>
      )}
      <p className="muted" style={{ fontSize: '.85rem', margin: '.4rem 0 0' }}>
        Two short beeps in your earbuds, over your music — it dips for a second
        and comes back. Only plays when headphones are connected, at your music
        volume.{' '}
        {status.headphones
          ? 'Headphones are connected now.'
          : 'No headphones connected right now.'}
      </p>
      <button
        className="secondary"
        disabled={testing}
        style={{ marginTop: '.5rem', padding: '.3rem .7rem', minHeight: 0, fontSize: '.85rem' }}
        onClick={() => void test()}
      >
        {testing ? 'Playing…' : 'Play test tone'}
      </button>
    </div>
  )
}
